import React, { useState, useEffect, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, Download, Trash2, ChevronLeft, ChevronRight, Maximize2, Minimize2 } from 'lucide-react';

const ImageModalViewer = ({ 
  image, 
  onClose, 
  onDelete = null,
  onNavigate = null,
  currentIndex = 0,
  totalImages = 1,
  canNavigate = false
}) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Keyboard navigation and controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (canNavigate && onNavigate) {
            onNavigate('prev');
          }
          break;
        case 'ArrowRight':
          if (canNavigate && onNavigate) {
            onNavigate('next');
          }
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
        case '_':
          handleZoomOut();
          break;
        case '0':
          resetView();
          break;
        case 'r':
        case 'R':
          handleRotate();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden'; // Prevent body scroll

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset'; // Restore body scroll
    };
  }, [onClose, canNavigate, onNavigate]);

  // Handle mouse wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.max(0.1, Math.min(5, prev + delta)));
  }, []);

  useEffect(() => {
    const modal = document.getElementById('image-modal-viewer');
    if (modal) {
      modal.addEventListener('wheel', handleWheel, { passive: false });
      return () => modal.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  // Zoom controls
  const handleZoomIn = () => {
    setZoom(prev => Math.min(5, prev + 0.2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(0.1, prev - 0.2));
  };

  const resetView = () => {
    setZoom(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  // Drag functionality
  const handleMouseDown = (e) => {
    if (e.target.tagName === 'IMG') {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Download image
  const handleDownload = () => {
    if (!image?.data) return;

    try {
      const link = document.createElement('a');
      const imageData = image.data.startsWith('data:') 
        ? image.data 
        : `data:${image.mime_type || 'image/jpeg'};base64,${image.data}`;
      
      link.href = imageData;
      link.download = image.file_name || `image_${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading image:', error);
      alert('Failed to download image. Please try again.');
    }
  };

  // Handle delete
  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      if (onDelete) {
        onDelete(image.id);
      }
    }
  };

  // Navigation
  const handlePrevious = () => {
    if (canNavigate && onNavigate) {
      onNavigate('prev');
      resetView(); // Reset view when navigating
    }
  };

  const handleNext = () => {
    if (canNavigate && onNavigate) {
      onNavigate('next');
      resetView(); // Reset view when navigating
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (!image) return null;

  const imageData = image.data.startsWith('data:') 
    ? image.data 
    : `data:${image.mime_type || 'image/jpeg'};base64,${image.data}`;

  const modalClasses = isFullscreen 
    ? 'fixed inset-0 bg-black z-50 flex items-center justify-center'
    : 'fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4';

  const imageContainerClasses = isFullscreen
    ? 'relative w-full h-full flex items-center justify-center'
    : 'relative max-w-4xl max-h-full bg-white rounded-lg overflow-hidden';

  return (
    <div
      id="image-modal-viewer"
      className={modalClasses}
      onClick={onClose}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div 
        className={imageContainerClasses}
        onClick={(e) => e.stopPropagation()}
      >
        {!isFullscreen && (
          <div className="absolute top-0 left-0 right-0 bg-gray-900 bg-opacity-75 text-white p-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-4">
              <h3 className="text-lg font-medium truncate max-w-md">
                {image.field_name || image.file_name || 'Image'}
              </h3>
              {canNavigate && totalImages > 1 && (
                <span className="text-sm text-gray-300">
                  {currentIndex + 1} of {totalImages}
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Navigation controls */}
              {canNavigate && totalImages > 1 && (
                <>
                  <button
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}
                    className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Previous image (←)"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={currentIndex === totalImages - 1}
                    className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Next image (→)"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* View controls */}
              <button
                onClick={toggleFullscreen}
                className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? (
                  <Minimize2 className="w-5 h-5" />
                ) : (
                  <Maximize2 className="w-5 h-5" />
                )}
              </button>

              {/* Zoom controls */}
              <button
                onClick={handleZoomOut}
                className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                title="Zoom out (-)"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              <span className="text-sm min-w-12 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                title="Zoom in (+)"
              >
                <ZoomIn className="w-5 h-5" />
              </button>

              <button
                onClick={handleRotate}
                className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                title="Rotate (R)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>

              <button
                onClick={resetView}
                className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                title="Reset view (0)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
              </button>

              {/* Download button */}
              <button
                onClick={handleDownload}
                className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                title="Download image"
              >
                <Download className="w-5 h-5" />
              </button>

              {/* Delete button */}
              {onDelete && (
                <button
                  onClick={handleDelete}
                  className="p-2 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
                  title="Delete image"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}

              {/* Close button */}
              <button
                onClick={onClose}
                className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                title="Close (Esc)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Image display area */}
        <div 
          className="flex items-center justify-center"
          style={{ 
            height: isFullscreen ? '100vh' : 'calc(100vh - 200px)',
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleMouseDown}
        >
          <img
            src={imageData}
            alt={image.field_name || image.file_name || 'Full size image'}
            className="max-w-full max-h-full object-contain select-none"
            style={{
              transform: `scale(${zoom}) rotate(${rotation}deg) translate(${position.x}px, ${position.y}px)`,
              transition: isDragging ? 'none' : 'transform 0.2s ease-out'
            }}
            draggable={false}
          />
        </div>

        {/* Image metadata */}
        {!isFullscreen && image && (
          <div className="absolute bottom-0 left-0 right-0 bg-gray-900 bg-opacity-75 text-white p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-300">Filename</p>
                <p className="font-medium truncate">{image.file_name || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-gray-300">Size</p>
                <p className="font-medium">
                  {image.size ? `${(image.size / 1024).toFixed(1)} KB` : 'Unknown'}
                </p>
              </div>
              <div>
                <p className="text-gray-300">Type</p>
                <p className="font-medium">{image.mime_type || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-gray-300">Uploaded</p>
                <p className="font-medium">
                  {image.upload_timestamp 
                    ? new Date(image.upload_timestamp).toLocaleDateString()
                    : 'Unknown'
                  }
                </p>
              </div>
            </div>
            {image.description && (
              <div className="mt-2">
                <p className="text-gray-300">Description</p>
                <p className="font-medium">{image.description}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageModalViewer;